import { Component, computed, inject } from '@angular/core';
import { RenderStatusService } from './render-status.service';

@Component({
  selector: 'app-render-loader',
  standalone: true,
  imports: [],
  template: `
    @if (visible()) {
      <div class="render-loader-backdrop" role="dialog" aria-modal="true" aria-live="assertive">
        <div class="render-loader-card">

          <!-- Spinner -->
          <svg class="render-loader__spinner" width="52" height="52" viewBox="0 0 52 52" aria-hidden="true">
            <circle cx="26" cy="26" r="21" fill="none"
                    stroke="var(--render-loader-track, rgba(255,255,255,0.14))"
                    stroke-width="5"></circle>
            <path d="M26 5 a21 21 0 0 1 21 21"
                  fill="none"
                  stroke="var(--color-primary, #f5c451)"
                  stroke-width="5"
                  stroke-linecap="round">
              <animateTransform attributeName="transform"
                                type="rotate"
                                from="0 26 26"
                                to="360 26 26"
                                dur="0.85s"
                                repeatCount="indefinite">
              </animateTransform>
            </path>
          </svg>

          <!-- Title -->
          <p class="render-loader__title">{{ status().title }}</p>

          <!-- Sub-text -->
          @if (status().sub) {
            <p class="render-loader__sub">{{ status().sub }}</p>
          }

          <!-- Progress bar -->
          @if (status().progress >= 0) {
            <div class="render-loader__bar-track" role="progressbar"
                 [attr.aria-valuenow]="progressPct()"
                 aria-valuemin="0" aria-valuemax="100">
              <div class="render-loader__bar-fill" [style.width.%]="progressPct()"></div>
            </div>
          } @else {
            <div class="render-loader__bar-track render-loader__bar-track--indeterminate" role="progressbar"
                 aria-valuetext="Loading">
              <div class="render-loader__bar-fill render-loader__bar-fill--indeterminate"></div>
            </div>
          }

          <!-- Foreground warning — only during active render -->
          @if (phase() === 'rendering') {
            <p class="render-loader__fg-note">Keep this tab in the foreground for best results</p>
          }

          <!-- Technical details -->
          @if (status().encoder || status().device) {
            <p class="render-loader__meta">
              @if (status().encoder) { <span>{{ status().encoder }}</span> }
              @if (status().encoder && status().device) { <span class="render-loader__meta-sep"> &middot; </span> }
              @if (status().device) { <span>{{ status().device }}</span> }
            </p>
          }

          <!-- Error message -->
          @if (phase() === 'error' && status().error) {
            <p class="render-loader__error">{{ status().error }}</p>
          }

          @if (phase() === 'error') {
            <div class="render-loader__actions">
              @if (status().retryable) {
                <button class="render-loader__retry" type="button" (click)="retry()">Retry</button>
              }
              <button class="render-loader__cancel" type="button" (click)="close()">Close</button>
            </div>
          } @else if (status().cancelable) {
            <button class="render-loader__cancel" type="button" (click)="cancel()">Cancel</button>
          }

        </div>
      </div>
    }
  `,
  styles: [`
    .render-loader-backdrop {
      position: fixed;
      inset: 0;
      z-index: 9000;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(8, 10, 18, 0.72);
      backdrop-filter: blur(3px);
      -webkit-backdrop-filter: blur(3px);
    }

    .render-loader-card {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 14px;
      padding: 36px 40px;
      min-width: 280px;
      max-width: 420px;
      background: var(--surface-card, #1a1c2a);
      border: 1px solid var(--surface-border, rgba(255,255,255,0.08));
      border-radius: 16px;
      box-shadow: 0 24px 64px rgba(0,0,0,0.55);
      text-align: center;
      color: var(--text-color, #e8eaf0);
    }

    .render-loader__spinner {
      flex-shrink: 0;
    }

    .render-loader__title {
      margin: 0;
      font-size: 15px;
      font-weight: 600;
      line-height: 1.4;
      color: var(--text-color, #e8eaf0);
    }

    .render-loader__sub {
      margin: 0;
      font-size: 12px;
      font-weight: 400;
      line-height: 1.5;
      opacity: 0.65;
      color: var(--text-color, #e8eaf0);
    }

    /* Progress bar */
    .render-loader__bar-track {
      width: 100%;
      height: 4px;
      border-radius: 2px;
      background: var(--surface-border, rgba(255,255,255,0.12));
      overflow: hidden;
    }

    .render-loader__bar-fill {
      height: 100%;
      border-radius: 2px;
      background: var(--color-primary, #f5c451);
      transition: width 0.2s ease;
    }

    /* Indeterminate animation via CSS (no keyframe name conflicts — scoped to component) */
    .render-loader__bar-track--indeterminate {
      position: relative;
    }

    .render-loader__bar-fill--indeterminate {
      position: absolute;
      width: 40%;
      animation: render-loader-slide 1.4s ease-in-out infinite;
    }

    @keyframes render-loader-slide {
      0%   { left: -40%; }
      100% { left: 100%; }
    }

    .render-loader__fg-note {
      margin: 0;
      font-size: 11px;
      font-weight: 400;
      opacity: 0.5;
      color: var(--text-color, #e8eaf0);
    }

    .render-loader__meta {
      margin: 0;
      font-size: 11px;
      font-weight: 400;
      opacity: 0.45;
      color: var(--text-color, #e8eaf0);
      font-variant-numeric: tabular-nums;
    }

    .render-loader__meta-sep {
      margin: 0 2px;
    }

    .render-loader__error {
      margin: 0;
      font-size: 12px;
      color: var(--color-danger, #f87171);
      font-weight: 500;
      word-break: break-word;
    }

    .render-loader__cancel {
      margin-top: 4px;
      padding: 7px 22px;
      border: 1px solid var(--surface-border, rgba(255,255,255,0.18));
      border-radius: 8px;
      background: transparent;
      color: var(--text-color, #e8eaf0);
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      transition: background 0.15s, border-color 0.15s;
    }

    .render-loader__cancel:hover {
      background: rgba(255,255,255,0.07);
      border-color: rgba(255,255,255,0.3);
    }

    .render-loader__cancel:active {
      background: rgba(255,255,255,0.12);
    }

    .render-loader__actions {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
    }

    .render-loader__retry {
      margin-top: 4px;
      padding: 8px 24px;
      border: none;
      border-radius: 8px;
      background: var(--color-primary, #f5c451);
      color: #17140b;
      font-size: 13px;
      font-weight: 700;
      cursor: pointer;
    }
  `]
})
export class RenderLoaderComponent {
  private readonly renderStatus = inject(RenderStatusService);

  protected readonly visible = this.renderStatus.visible;
  protected readonly status = this.renderStatus.status;
  protected readonly phase = computed(() => this.renderStatus.status().phase);
  protected readonly progressPct = computed(() => Math.round(this.renderStatus.status().progress * 100));

  protected cancel(): void {
    this.renderStatus.requestCancel();
  }

  protected retry(): void {
    this.renderStatus.requestRetry();
  }

  protected close(): void {
    this.renderStatus.reset();
  }
}
